import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { getApiUrl } from '@/lib/api';

const NotificationContext = createContext(null);

const API = `${getApiUrl()}/api`;

export function NotificationProvider({ children }) {
  const { user, isDriver } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const lastCheckRef = useRef(null);
  const audioRef = useRef(null);

  // Create audio element for notification sound (default text tone)
  useEffect(() => {
    // Simple beep sound - like a text message
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    audioRef.current = audioContext;
  }, []);

  const playSound = () => {
    try {
      // Play a simple text-tone beep
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 880; // A5 note
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
      
      // Second beep
      setTimeout(() => {
        const osc2 = audioContext.createOscillator();
        const gain2 = audioContext.createGain();
        osc2.connect(gain2);
        gain2.connect(audioContext.destination);
        osc2.frequency.value = 880;
        osc2.type = 'sine';
        gain2.gain.setValueAtTime(0.3, audioContext.currentTime);
        gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
        osc2.start(audioContext.currentTime);
        osc2.stop(audioContext.currentTime + 0.3);
      }, 150);
    } catch (e) {
      console.log('Audio not supported');
    }
  };

  const addNotification = useCallback((notification) => {
    const id = Date.now() + Math.random();
    const newNotif = {
      id,
      ...notification,
      timestamp: new Date().toISOString(),
      read: false
    };
    
    setNotifications(prev => [newNotif, ...prev.slice(0, 49)]); // Keep last 50
    setUnreadCount(prev => prev + 1);
    playSound();

    // Auto-dismiss after 5 seconds if it's a toast
    if (notification.autoDismiss !== false) {
      setTimeout(() => {
        dismissNotification(id);
      }, 5000);
    }

    return id;
  }, []);

  const dismissNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const markAsRead = useCallback((id) => {
    setNotifications(prev => prev.map(n => 
      n.id === id ? { ...n, read: true } : n
    ));
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  // Poll for new deliveries (for drivers)
  useEffect(() => {
    if (!user || !isDriver) return;

    const checkForNewDeliveries = async () => {
      try {
        const response = await axios.get(`${API}/driver/available-deliveries`);
        const deliveries = response.data;
        
        if (lastCheckRef.current !== null) {
          // Check for new deliveries since last check
          const newDeliveries = deliveries.filter(d => {
            const createdAt = new Date(d.created_at).getTime();
            return createdAt > lastCheckRef.current;
          });

          newDeliveries.forEach(delivery => {
            addNotification({
              type: 'new_delivery',
              title: '🚗 New Delivery!',
              message: `${delivery.service_type} - ${delivery.customer_name}`,
              subtitle: `$${(delivery.driver_payout || delivery.estimated_price * 0.8).toFixed(2)} payout`,
              data: delivery,
              autoDismiss: false
            });
          });
        }
        
        lastCheckRef.current = Date.now();
      } catch (err) {
        console.error('Failed to check deliveries:', err);
      }
    };

    // Check immediately
    checkForNewDeliveries();
    
    // Then check every 30 seconds
    const interval = setInterval(checkForNewDeliveries, 30000);
    
    return () => clearInterval(interval);
  }, [user, isDriver, addNotification]);

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      addNotification,
      dismissNotification,
      markAsRead,
      clearAll
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
